import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";
import {
  FaTrophy,
  FaLock,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaDollarSign,
  FaUsers,
  FaFire,
  FaClock,
  FaInfoCircle,
  FaStar,
  FaRocket,
} from "react-icons/fa";

const Stages = () => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingStage, setClaimingStage] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    fetchStageProgress();
  }, []);

  const fetchStageProgress = async () => {
    try {
      const response = await api.get("/stages/progress");
      setProgress(response.data);
    } catch (error) {
      toast.error("Failed to load stage progress");
    } finally {
      setLoading(false);
    }
  };

  const handleClaimStage = async (stageNumber) => {
    setClaimingStage(stageNumber);
    try {
      const response = await api.post(`/stages/claim/${stageNumber}`);
      toast.success(response.data.message);
      await fetchStageProgress();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to claim stage");
    } finally {
      setClaimingStage(null);
    }
  };

  const getProgressPct = (stage) =>
    Math.min((stage.currentMembers / stage.requiredMembers) * 100, 100);

  // SVG ring progress for hero
  const ringProgress =
    progress ?
      Math.round(
        ((progress.stagesCompleted || 0) / (progress.totalStages || 26)) * 100,
      )
    : 0;
  const circumference = 2 * Math.PI * 42;
  const strokeDashoffset = circumference - (ringProgress / 100) * circumference;

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <div className="w-12 h-12 rounded-full border-[3px] border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <p className="text-sm text-slate-400 animate-pulse">
          Loading stages...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      {/* Hero Card with Ring Progress */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 p-5 shadow-xl shadow-orange-500/10">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            {/* Ring Progress */}
            <div className="relative flex-shrink-0">
              <svg
                width="96"
                height="96"
                viewBox="0 0 96 96"
                className="transform -rotate-90"
              >
                <circle
                  cx="48"
                  cy="48"
                  r="42"
                  fill="none"
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="6"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="42"
                  fill="none"
                  stroke="white"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-extrabold text-white leading-none">
                  {progress?.stagesCompleted || 0}
                </span>
                <span className="text-[9px] text-white/70 font-semibold">
                  of {progress?.totalStages || 26}
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <FaTrophy className="text-sm text-white/80" />
                <h1 className="text-lg font-bold text-white">Stage Journey</h1>
              </div>
              <p className="text-[11px] text-orange-100/80 font-medium mb-3">
                Climb through 26 stages to earn up to $10,000
              </p>

              <div className="bg-white/15 backdrop-blur-xl rounded-xl px-3.5 py-2.5 border border-white/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-orange-100 font-medium uppercase tracking-wider">
                      Stage Earnings
                    </p>
                    <p className="text-xl font-extrabold text-white tracking-tight">
                      ${progress?.totalStageEarnings?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <FaDollarSign className="text-sm text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat Chips */}
      <div className="relative">
        <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none rounded-r-2xl" />
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {[
            {
              label: "Current Stage",
              value: progress?.currentStage || 0,
              icon: FaFire,
              gradient: "from-amber-500 to-orange-500",
              glow: "shadow-amber-500/20",
            },
            {
              label: "Position",
              value: `#${progress?.userPosition || 0}`,
              icon: FaStar,
              gradient: "from-violet-500 to-purple-600",
              glow: "shadow-violet-500/20",
            },
            {
              label: "Users Below",
              value: progress?.usersBelow ?? progress?.totalSystemMembers ?? 0,
              icon: FaUsers,
              gradient: "from-sky-500 to-cyan-600",
              glow: "shadow-sky-500/20",
            },
            {
              label: "Completed",
              value: `${progress?.stagesCompleted || 0}/${progress?.totalStages || 26}`,
              icon: FaCheck,
              gradient: "from-emerald-500 to-teal-600",
              glow: "shadow-emerald-500/20",
            },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className={`snap-start flex-shrink-0 w-[120px] rounded-xl bg-gradient-to-br ${stat.gradient} p-3.5 shadow-lg ${stat.glow} border border-white/10`}
              >
                <Icon className="text-white/70 text-xs mb-2" />
                <p className="text-lg font-extrabold text-white leading-none">
                  {stat.value}
                </p>
                <p className="text-[10px] text-white/70 font-medium mt-1">
                  {stat.label}
                </p>
              </div>
            );
          })}
          <div className="flex-shrink-0 w-4" />
        </div>
      </div>

      {/* Stage Timeline */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <FaRocket className="text-cyan-400 text-xs" />
          Your Progress
        </h3>

        <div className="space-y-0">
          {progress?.stages &&
            progress.stages.map((stage, idx) => {
              const pct = getProgressPct(stage);
              const isExpanded = expandedStage === stage.stageNumber;
              const isLast = idx === progress.stages.length - 1;

              // Stage milestone styling
              const stageStyle =
                stage.isClaimed ?
                  {
                    dot: "bg-emerald-400 shadow-emerald-400/40",
                    line: "bg-emerald-400/30",
                    ring: "ring-emerald-400/20",
                  }
                : stage.isEligible ?
                  {
                    dot: "bg-cyan-400 shadow-cyan-400/40 animate-pulse",
                    line: "bg-cyan-400/20",
                    ring: "ring-cyan-400/20",
                  }
                : {
                    dot: "bg-slate-600",
                    line: "bg-white/[0.06]",
                    ring: "ring-white/[0.06]",
                  };

              return (
                <div key={stage.stageNumber} className="flex gap-3">
                  {/* Timeline spine */}
                  <div className="flex flex-col items-center flex-shrink-0 w-8">
                    {/* Dot / Node */}
                    <div
                      className={`w-5 h-5 rounded-full ${stageStyle.dot} shadow-lg ${stageStyle.ring} ring-4 flex items-center justify-center z-10`}
                    >
                      {stage.isClaimed ?
                        <FaCheck className="text-[7px] text-white" />
                      : stage.isEligible ?
                        <FaStar className="text-[7px] text-white" />
                      : <FaLock className="text-[6px] text-slate-400" />}
                    </div>
                    {/* Connecting line */}
                    {!isLast && (
                      <div
                        className={`w-0.5 flex-1 min-h-[16px] ${stageStyle.line}`}
                      />
                    )}
                  </div>

                  {/* Stage content card */}
                  <div className={`flex-1 mb-2 ${isLast ? "" : "pb-2"}`}>
                    <button
                      onClick={() =>
                        setExpandedStage(isExpanded ? null : stage.stageNumber)
                      }
                      className={`w-full text-left px-3.5 py-3 rounded-xl border transition-all duration-200 active:scale-[0.99] touch-manipulation ${
                        stage.isClaimed ?
                          "bg-emerald-500/[0.06] border-emerald-500/20"
                        : stage.isEligible ?
                          "bg-cyan-500/[0.06] border-cyan-500/20"
                        : "bg-white/[0.02] border-white/[0.05]"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-white">
                              Stage {stage.stageNumber}
                            </span>
                            <span
                              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                                stage.isClaimed ?
                                  "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
                                : stage.isEligible ?
                                  "text-cyan-300 bg-cyan-500/15 border-cyan-500/30"
                                : "text-slate-500 bg-white/[0.04] border-white/[0.06]"
                              }`}
                            >
                              {stage.isClaimed ?
                                "Claimed"
                              : stage.isEligible ?
                                "Ready"
                              : "Locked"}
                            </span>
                          </div>
                          {/* Mini progress bar */}
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="flex-1 h-1 rounded-full bg-white/[0.08] overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  stage.isClaimed ? "bg-emerald-400"
                                  : stage.isEligible ? "bg-cyan-400"
                                  : "bg-slate-600"
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-500 font-medium flex-shrink-0">
                              {stage.currentMembers}/{stage.requiredMembers}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span
                            className={`text-sm font-extrabold ${
                              stage.isClaimed ? "text-emerald-400"
                              : stage.isEligible ? "text-cyan-400"
                              : "text-slate-500"
                            }`}
                          >
                            ${stage.payout}
                          </span>
                          {isExpanded ?
                            <FaChevronUp className="text-[9px] text-slate-500" />
                          : <FaChevronDown className="text-[9px] text-slate-500" />
                          }
                        </div>
                      </div>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div
                        className={`mt-1.5 px-3.5 py-3 rounded-xl border space-y-3 animate-in slide-in-from-top-1 duration-200 ${
                          stage.isClaimed ?
                            "bg-emerald-500/[0.04] border-emerald-500/15"
                          : stage.isEligible ?
                            "bg-cyan-500/[0.04] border-cyan-500/15"
                          : "bg-white/[0.02] border-white/[0.04]"
                        }`}
                      >
                        {/* Detail rows */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="px-2.5 py-2 rounded-lg bg-white/[0.04]">
                            <p className="text-[10px] text-slate-500 font-medium">
                              Required
                            </p>
                            <p className="text-xs font-bold text-white">
                              {stage.requiredMembers} users
                            </p>
                          </div>
                          <div className="px-2.5 py-2 rounded-lg bg-white/[0.04]">
                            <p className="text-[10px] text-slate-500 font-medium">
                              Current
                            </p>
                            <p className="text-xs font-bold text-white">
                              {stage.currentMembers} users
                            </p>
                          </div>
                          <div className="px-2.5 py-2 rounded-lg bg-white/[0.04]">
                            <p className="text-[10px] text-slate-500 font-medium">
                              Payout
                            </p>
                            <p
                              className={`text-xs font-bold ${stage.isClaimed ? "text-emerald-400" : "text-cyan-400"}`}
                            >
                              ${stage.payout}
                            </p>
                          </div>
                          <div className="px-2.5 py-2 rounded-lg bg-white/[0.04]">
                            <p className="text-[10px] text-slate-500 font-medium">
                              Progress
                            </p>
                            <p className="text-xs font-bold text-white">
                              {Math.round(pct)}%
                            </p>
                          </div>
                        </div>

                        {/* Reason / Wait time */}
                        {!stage.isClaimed &&
                          !stage.isEligible &&
                          stage.reason && (
                            <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-rose-500/[0.06] border border-rose-500/15">
                              <FaClock className="text-[10px] text-rose-400 mt-0.5 flex-shrink-0" />
                              <p className="text-[11px] text-rose-300 leading-relaxed">
                                {stage.reason}
                                {stage.daysRemaining > 0 && (
                                  <span className="font-semibold">
                                    {" "}
                                    ({stage.daysRemaining}d remaining)
                                  </span>
                                )}
                              </p>
                            </div>
                          )}

                        {/* Claim button */}
                        {stage.isEligible && !stage.isClaimed && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClaimStage(stage.stageNumber);
                            }}
                            disabled={claimingStage === stage.stageNumber}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation disabled:opacity-50"
                          >
                            {claimingStage === stage.stageNumber ?
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            : <>
                                <FaTrophy className="text-[10px]" />
                                Claim ${stage.payout}
                              </>
                            }
                          </button>
                        )}

                        {/* Claimed confirmation */}
                        {stage.isClaimed && (
                          <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                            <FaCheck className="text-[10px] text-emerald-400" />
                            <span className="text-xs font-semibold text-emerald-300">
                              Successfully Claimed
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* How it works - Collapsible */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="w-full flex items-center justify-between p-4 touch-manipulation"
        >
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FaInfoCircle className="text-cyan-400 text-xs" />
            How Stages Work
          </h3>
          {showInfo ?
            <FaChevronUp className="text-xs text-slate-500" />
          : <FaChevronDown className="text-xs text-slate-500" />}
        </button>
        {showInfo && (
          <div className="px-4 pb-4 space-y-2 animate-in slide-in-from-top-1 duration-200">
            {[
              "Each stage requires a specific number of users below your position",
              "Your position is assigned upon activation — earlier = more users needed",
              "You must have at least 1 direct recruit to qualify",
              "Wait 7 days between claiming consecutive stages",
              "Payouts increase as you progress through stages",
              "Total potential earnings across all 26 stages: $10,000",
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FaCheck className="text-[8px] text-amber-400" />
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Stages;
